/**
 * Alert Actions Utility
 * Higher-level alert action handlers for common UI operations
 */

import alertService from '../services/alertService';
import { formatForNotification } from './alertHelpers';

/**
 * Handle alert click with tracking and navigation
 * @param {Object} alert - Alert object
 * @param {Object} options - Action options
 * @param {Function} options.navigate - Navigation function
 * @param {Function} options.onMarkRead - Callback after marking read
 * @param {Function} options.onError - Error callback
 */
export async function handleAlertClick(alert, options = {}) {
  const { navigate, onMarkRead, onError } = options;

  try {
    // Track the click (also marks as read)
    await alertService.trackClick(alert.id);

    // Call callback
    if (onMarkRead) {
      onMarkRead(alert.id);
    }

    // Navigate if URL provided
    if (alert.action_url && navigate) {
      // Check if it's an internal or external URL
      if (alert.action_url.startsWith('http')) {
        window.open(alert.action_url, '_blank');
      } else {
        navigate(alert.action_url);
      }
    }
  } catch (error) {
    console.error('Error handling alert click:', error);
    if (onError) {
      onError(error);
    }
  }
}

/**
 * Handle alert dismissal
 * @param {string} alertId - Alert ID
 * @param {Object} options - Action options
 * @param {Function} options.onDismiss - Callback after dismissing
 * @param {Function} options.onError - Error callback
 */
export async function handleAlertDismiss(alertId, options = {}) {
  const { onDismiss, onError } = options;

  try {
    await alertService.dismissAlert(alertId);

    if (onDismiss) {
      onDismiss(alertId);
    }
  } catch (error) {
    console.error('Error dismissing alert:', error);
    if (onError) {
      onError(error);
    }
  }
}

/**
 * Handle marking alert as read
 * @param {string} alertId - Alert ID
 * @param {Object} options - Action options
 * @param {Function} options.onMarkRead - Callback after marking read
 * @param {Function} options.onError - Error callback
 */
export async function handleMarkAsRead(alertId, options = {}) {
  const { onMarkRead, onError } = options;

  try {
    await alertService.markAsRead(alertId);

    if (onMarkRead) {
      onMarkRead(alertId);
    }
  } catch (error) {
    console.error('Error marking alert as read:', error);
    if (onError) {
      onError(error);
    }
  }
}

/**
 * Handle marking all alerts as read
 * @param {Object} options - Action options
 * @param {Function} options.onComplete - Callback after marking all read
 * @param {Function} options.onError - Error callback
 */
export async function handleMarkAllAsRead(options = {}) {
  const { onComplete, onError } = options;

  try {
    const count = await alertService.markAllAsRead();

    if (onComplete) {
      onComplete(count);
    }

    return count;
  } catch (error) {
    console.error('Error marking all alerts as read:', error);
    if (onError) {
      onError(error);
    }
    return 0;
  }
}

/**
 * Handle batch delivery marking
 * @param {string[]} alertIds - Array of alert IDs
 * @param {Object} options - Action options
 */
export async function handleMarkDelivered(alertIds, options = {}) {
  const { onComplete, onError } = options;

  try {
    const count = await alertService.markAlertsDelivered(alertIds);

    if (onComplete) {
      onComplete(count);
    }

    return count;
  } catch (error) {
    console.error('Error marking alerts as delivered:', error);
    if (onError) {
      onError(error);
    }
    return 0;
  }
}

/**
 * Request browser notification permission
 * @returns {Promise<string>} Permission status
 */
export async function requestNotificationPermission() {
  if (!('Notification' in window)) {
    return 'unsupported';
  }

  if (Notification.permission === 'granted') {
    return 'granted';
  }

  if (Notification.permission !== 'denied') {
    const permission = await Notification.requestPermission();
    return permission;
  }

  return Notification.permission;
}

/**
 * Show browser notification for an alert
 * @param {Object} alert - Alert object
 * @param {Object} options - Notification options
 */
export function showBrowserNotification(alert, options = {}) {
  if (!('Notification' in window)) return;
  if (Notification.permission !== 'granted') return;

  const notificationData = formatForNotification(alert);

  const notification = new Notification(notificationData.title, {
    body: notificationData.body,
    icon: options.icon || '/favicon.ico',
    tag: notificationData.tag,
    requireInteraction: alert.priority >= 3,
    ...options
  });

  notification.onclick = () => {
    window.focus();
    if (notificationData.data?.url) {
      window.location.href = notificationData.data.url;
    }
    notification.close();
  };

  return notification;
}

/**
 * Create alert action button configs based on alert type
 * @param {Object} alert - Alert object
 * @returns {Array} Array of action button configs
 */
export function getAlertActions(alert) {
  const baseActions = [
    {
      id: 'markRead',
      label: alert.is_read ? 'Mark Unread' : 'Mark Read',
      icon: alert.is_read ? 'EyeOff' : 'Eye',
      action: 'toggleRead'
    },
    {
      id: 'dismiss',
      label: 'Dismiss',
      icon: 'X',
      action: 'dismiss'
    }
  ];

  // Add type-specific actions
  switch (alert.alert_type) {
    case 'profile_view':
    case 'interest_spike':
      return [
        {
          id: 'viewBuyer',
          label: 'View Buyer',
          icon: 'ExternalLink',
          primary: true,
          action: 'navigate',
          url: `/buyers/${alert.buyer_id}`
        },
        ...baseActions
      ];

    case 'meeting_request':
      return [
        {
          id: 'viewRequest',
          label: 'View Request',
          icon: 'Calendar',
          primary: true,
          action: 'navigate',
          url: alert.action_url || `/meetings`
        },
        ...baseActions
      ];

    case 'new_signal':
      return [
        {
          id: 'viewSignal',
          label: 'View Signal',
          icon: 'Radio',
          primary: true,
          action: 'navigate',
          url: alert.action_url || `/signals/${alert.related_signal_id}`
        },
        ...baseActions
      ];

    case 'match_score':
      return [
        {
          id: 'viewMatch',
          label: 'View Match',
          icon: 'Target',
          primary: true,
          action: 'navigate',
          url: `/buyers/${alert.buyer_id}`
        },
        ...baseActions
      ];

    default:
      if (alert.action_url) {
        return [
          {
            id: 'viewDetails',
            label: 'View Details',
            icon: 'ExternalLink',
            primary: true,
            action: 'navigate',
            url: alert.action_url
          },
          ...baseActions
        ];
      }
      return baseActions;
  }
}

/**
 * Execute an alert action
 * @param {Object} action - Action config from getAlertActions
 * @param {Object} alert - Alert object
 * @param {Object} handlers - Action handlers
 */
export async function executeAlertAction(action, alert, handlers = {}) {
  const { navigate, onMarkRead, onDismiss, onError } = handlers;

  switch (action.action) {
    case 'navigate':
      await handleAlertClick(alert, { navigate, onMarkRead, onError });
      break;

    case 'toggleRead':
      if (alert.is_read) {
        // For unread functionality, would need to add to service
        console.log('Mark unread not implemented');
      } else {
        await handleMarkAsRead(alert.id, { onMarkRead, onError });
      }
      break;

    case 'dismiss':
      await handleAlertDismiss(alert.id, { onDismiss, onError });
      break;

    default:
      console.warn('Unknown action:', action.action);
  }
}

/**
 * Batch process alerts
 * @param {string[]} alertIds - Array of alert IDs
 * @param {string} action - Action to perform ('markRead', 'dismiss')
 * @param {Object} options - Options
 */
export async function batchProcessAlerts(alertIds, action, options = {}) {
  const { onProgress, onComplete, onError } = options;
  const results = { success: 0, failed: 0 };

  for (let i = 0; i < alertIds.length; i++) {
    try {
      switch (action) {
        case 'markRead':
          await alertService.markAsRead(alertIds[i]);
          break;
        case 'dismiss':
          await alertService.dismissAlert(alertIds[i]);
          break;
      }
      results.success++;
    } catch (err) {
      results.failed++;
      if (onError) {
        onError(alertIds[i], err);
      }
    }

    if (onProgress) {
      onProgress(i + 1, alertIds.length, results);
    }
  }

  if (onComplete) {
    onComplete(results);
  }

  return results;
}

export default {
  handleAlertClick,
  handleAlertDismiss,
  handleMarkAsRead,
  handleMarkAllAsRead,
  handleMarkDelivered,
  requestNotificationPermission,
  showBrowserNotification,
  getAlertActions,
  executeAlertAction,
  batchProcessAlerts
};
